import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { useLive } from "../lib/use-live";
import { useDateTime } from "../lib/use-relative";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { useAuth } from "../context/auth";
import { useToast } from "../context/toast";
import { fromLocalInput, money, signedMoney, toLocalInput } from "../lib/format";
import {
  ActionButton,
  ActionIcon,
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Modal,
  NoRowActions,
  Panel,
  RowActions,
  Table,
  Td,
  Tr,
  TxStatusBadge,
} from "../components/ui";
import {
  DateTimeField,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/ui/form";
import {
  FilterBar,
  PageBar,
  SortHeader,
  useFilters,
  useSort,
} from "../components/ui/filters";
import type { LedgerRow, Paged } from "../types/api";

/*
 * The types a row can actually carry.
 *
 * `admin_adjustment` is gone from the filter because it is gone from the data —
 * rows an earlier version wrote are reclassified at boot, so a filter for it
 * could only ever return nothing. `credit_adjustment` takes its place for the
 * one thing it genuinely described: an operator setting a balance to a figure.
 */
const TYPE_VALUES = [
  "top_up",
  "withdrawal",
  "credit_adjustment",
  "bet_stake",
  "bet_payout",
  "bet_refund",
] as const;

const STATUS_VALUES = ["success", "processing", "pending", "failed", "rejected"] as const;

export function Ledger() {
  const dateTime = useDateTime();
  const t = useT();
  const typeOptions = [
    { value: "", label: t("ledger.anyType") },
    ...TYPE_VALUES.map((v) => ({ value: v, label: t(`tx.${v}` as TranslationKey) })),
  ];
  const statusOptions = [
    { value: "", label: t("common.anyStatus") },
    ...STATUS_VALUES.map((v) => ({ value: v, label: t(`txStatus.${v}` as TranslationKey) })),
  ];
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const filters = useFilters(
    { username: "", reference: "", type: "", status: "", from: "", to: "" },
    () => setPage(1),
  );
  const sort = useSort("createdAt", "desc", () => setPage(1));

  const rows = useLive<Paged<LedgerRow>>("ledger", {
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
        username: filters.applied.username || undefined,
        reference: filters.applied.reference || undefined,
        type: filters.applied.type || undefined,
        status: filters.applied.status || undefined,
        from: filters.applied.from
          ? new Date(filters.applied.from).toISOString()
          : undefined,
        to: filters.applied.to
          ? new Date(`${filters.applied.to}T23:59:59`).toISOString()
          : undefined,
      });

  const [editing, setEditing] = useState<LedgerRow | null>(null);
  const [deleting, setDeleting] = useState<LedgerRow | null>(null);

  return (
    <div className="space-y-5">
      <FilterBar
        loading={rows.loading}
        state={filters}
        filters={[
          {
            key: "username",
            label: t("players.filter.player"),
            placeholder: t("players.filter.playerPlaceholder"),
          },
          { key: "reference", label: t("col.reference"), placeholder: "WAL-2026…" },
          { key: "type", label: t("common.type"), type: "select", options: typeOptions },
          {
            key: "status",
            label: t("common.status"),
            type: "select",
            options: statusOptions,
          },
          { key: "from", label: t("common.from"), type: "date" },
          { key: "to", label: t("common.to"), type: "date" },
        ]}
      />

      <Panel
        eyebrow={t("nav.group.money")}
        title={t("nav.ledger")}
        bodyClassName=""
        actions={
          <Button size="sm" onClick={rows.reload}>
            {t("action.refresh")}
          </Button>
        }
      >
        {rows.error ? (
          <div className="p-5">
            <ErrorNote message={rows.error} onRetry={rows.reload} />
          </div>
        ) : (
          <>
            <Table
              minWidth="62rem"
              head={[
                <SortHeader key="r" field="reference" state={sort}>
                  {t("col.reference")}
                </SortHeader>,
                t("common.player"),
                <SortHeader key="t" field="type" state={sort}>
                  {t("common.type")}
                </SortHeader>,
                <SortHeader
                  key="a"
                  field="amountCents"
                  state={sort}
                  align="right"
                >
                  {t("common.amount")}
                </SortHeader>,
                <SortHeader
                  key="b"
                  field="balanceAfterCents"
                  state={sort}
                  align="right"
                >
                  {t("col.balanceAfter")}
                </SortHeader>,
                <SortHeader key="s" field="status" state={sort}>
                  {t("common.status")}
                </SortHeader>,
                <SortHeader key="c" field="createdAt" state={sort}>
                  {t("common.when")}
                </SortHeader>,
                <span key="a" className="block text-right">
                  {t("col.actions")}
                </span>,
              ]}
            >
              {rows.loading ? (
                <LoadingRows columns={8} />
              ) : rows.data && rows.data.items.length === 0 ? (
                <Tr>
                  <Td colSpan={8}>
                    <EmptyState
                      title={t("ledger.empty")}
                      detail={t("sessions.emptyDetail")}
                    />
                  </Td>
                </Tr>
              ) : (
                (rows.data?.items ?? []).map((row) => (
                  <Tr key={row.id}>
                    <Td className="money text-dim text-[11px]">
                      {row.reference}
                    </Td>
                    <Td>
                      {row.user ? (
                        <Link
                          to={`/players/${row.user.id}`}
                          className="hover:underline"
                        >
                          {row.user.username}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </Td>
                    <Td>
                      <Badge
                        tone={row.amountCents >= 0 ? "success" : "neutral"}
                      >
                        {t(`tx.${row.type}` as TranslationKey)}
                      </Badge>
                    </Td>
                    <Td
                      className="money"
                      style={{
                        color:
                          row.amountCents >= 0
                            ? "var(--success)"
                            : "var(--info)",
                      }}
                    >
                      {signedMoney(row.amountCents)}
                    </Td>
                    <Td className="money text-muted">
                      {money(row.balanceAfterCents)}
                    </Td>
                    <Td>
                      <TxStatusBadge status={row.status} />
                    </Td>
                    <Td className="text-dim text-[12px]">
                      {dateTime(row.createdAt)}
                    </Td>
                    <Td>
                      {can("admin") ? (
                        <RowActions>
                          <ActionButton
                            icon={ActionIcon.edit}
                            onClick={() => setEditing(row)}
                          >
                            {t("action.edit")}
                          </ActionButton>
                          <ActionButton
                            icon={ActionIcon.delete}
                            tone="danger"
                            onClick={() => setDeleting(row)}
                          >
                            {t("action.delete")}
                          </ActionButton>
                        </RowActions>
                      ) : (
                        <NoRowActions reason={t("action.noPermission")} />
                      )}
                    </Td>
                  </Tr>
                ))
              )}
            </Table>

            {rows.data ? (
              <PageBar
                page={rows.data.page}
                pages={rows.data.pages}
                total={rows.data.total}
                perPage={perPage}
                onPage={setPage}
                onPerPage={(size) => {
                  setPerPage(size);
                  setPage(1);
                }}
              />
            ) : null}
          </>
        )}
      </Panel>

      <EditRowDialog
        row={editing}
        onClose={() => setEditing(null)}
        onDone={rows.reload}
      />
      <DeleteRowDialog
        row={deleting}
        onClose={() => setDeleting(null)}
        onDone={rows.reload}
      />
    </div>
  );
}

function EditRowDialog({
  row,
  onClose,
  onDone,
}: {
  row: LedgerRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("success");
  const [happenedAt, setHappenedAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Seeded once, when the dialog is handed a row.
   *
   * Keyed on the row rather than on every render: the screen's subscription is
   * re-answered on a ten-second heartbeat, and re-seeding on each answer would
   * wipe a half-made correction out from under whoever was typing it. `useLive`
   * keeps the object's identity while the data is unchanged, so this fires when
   * the dialog opens and not on the beat.
   *
   * Status is seeded here too. It was not before, and defaulted to "success" —
   * so opening this dialog on a failed row to fix its wording and pressing Save
   * quietly marked the movement successful.
   */
  useEffect(() => {
    if (!row) return;
    setReason(row.reason ?? "");
    setStatus(row.status);
    setHappenedAt(toLocalInput(row.createdAt));
  }, [row]);

  const happenedAtIso = fromLocalInput(happenedAt);
  const happenedAtValid = happenedAt === "" ? false : happenedAtIso !== null;

  async function submit() {
    if (!row || !happenedAtValid) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateTransaction(row.id, {
        reason: reason.trim(),
        status,
        // Only when it actually moved: the field holds minutes and the row
        // usually carries seconds, so sending it unconditionally would round
        // every timestamp down the first time anybody edited the reason.
        createdAt:
          happenedAt === toLocalInput(row.createdAt)
            ? undefined
            : (happenedAtIso ?? undefined),
      });
      toast.success(t("toast.rowUpdated"));
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.updateRow"),
      );
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setReason("");
    setStatus("success");
    setHappenedAt("");
    setError(null);
    onClose();
  }

  return (
    <Modal
      open={Boolean(row)}
      onClose={close}
      eyebrow={t("nav.group.money")}
      title={`Edit ${row?.reference ?? ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="gold"
            onClick={submit}
            loading={busy}
            disabled={!happenedAtValid}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">
{t("ledger.amountFixedNote")}
        </p>
        <div
          className="rounded-lg px-4 py-3"
          style={{ background: "var(--surface-inset)" }}
        >
          <p className="money text-[15px]">
            {signedMoney(row?.amountCents ?? 0)}
          </p>
          <p className="text-dim mt-1 text-[11px]">
            {row ? t(`tx.${row.type}` as TranslationKey) : "—"}
          </p>
        </div>
        <SelectField
          label={t("common.status")}
          value={status}
          onChange={setStatus}
          options={STATUS_VALUES.map((v) => ({
            value: v,
            label: t(`txStatus.${v}` as TranslationKey),
          }))}
        />
        <DateTimeField
          label={t("ledger.edit.happenedAt")}
          value={happenedAt}
          onChange={setHappenedAt}
          error={
            !happenedAtValid && happenedAt !== ""
              ? t("ledger.edit.happenedAtError")
              : null
          }
          hint={t("ledger.edit.happenedAtHint")}
          required
        />
        <TextAreaField label={t("common.reason")} value={reason} onChange={setReason} />
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function DeleteRowDialog({
  row,
  onClose,
  onDone,
}: {
  row: LedgerRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!row) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.deleteTransaction(
        row.id,
        reason.trim() || undefined,
      );
      toast.success(
        t("toast.rowDeleted"),
        t("toast.balanceCorrected", { amount: money(result.balanceCents ?? 0) }),
      );
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.deleteRow"),
      );
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setReason("");
    setError(null);
    onClose();
  }

  return (
    <Modal
      open={Boolean(row)}
      onClose={close}
      eyebrow={t("nav.group.money")}
      title={`Delete ${row?.reference ?? ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button variant="danger" onClick={submit} loading={busy}>
            {t("ledger.delete.submit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">
          Removing this row also moves the player's balance by{" "}
          <span className="money">{signedMoney(-(row?.amountCents ?? 0))}</span>
          , in the same step. The cached balance is the sum of this ledger, so
          the two have to change together.
        </p>
        <TextField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          hint={t("common.reasonHint")}
          autoFocus
        />
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}
